import { useEffect, useState } from "react";
import {
  AlignItems,
  Box,
  Color,
  ColorPreset,
  ColorScheme,
  FontWeight,
  Interpolation,
  JustifyContent,
  MoneyText,
  Space,
  Tag,
  TagColor,
  TagSize,
  TagVariant,
  Text,
} from "@gocardless/flux-react";

import { Currency } from "src/common/currencies";
import { useCounter } from "src/hooks/useCounter";
import { useI18n } from "src/technical-integrations/i18n";

export interface PaymentTypeTagConfigs {
  baseColor: TagColor;
  transitionColor: TagColor;
}

export interface TransactionCardProps {
  title: string;
  maxAmount: number;
  paymentSchedule: string;
  paymentType: string;
  isAnimationApplied: boolean;
  onAnimationFinished: Function;
  counterDelay: number;
  restartAnimation: boolean;
  onRestart?: Function;
  paymentTypeTagConfig: PaymentTypeTagConfigs;
  animation: Interpolation;
}

export const TRANSACTION_BOX_DEFAULT_SCALE = 0.7;

export const TransactionBox: React.FC<TransactionCardProps> = ({
  title,
  maxAmount,
  paymentType,
  paymentSchedule,
  onAnimationFinished,
  isAnimationApplied,
  counterDelay,
  restartAnimation,
  onRestart,
  paymentTypeTagConfig,
  animation,
}: TransactionCardProps) => {
  const { baseColor, transitionColor } = paymentTypeTagConfig;
  const [isAnimated, setIsAnimated] = useState(isAnimationApplied);
  const [tagColor, setTagColor] = useState(baseColor);
  const [tagOpacity, setTagOpacity] = useState("0.5");
  const [locale] = useI18n();

  const { counter, resetCounter } = useCounter({
    maximum: maxAmount,
    delay: isAnimated ? counterDelay : null,
    startCounter: isAnimated,
  });

  useEffect(() => {
    setIsAnimated(isAnimationApplied);
  }, [isAnimationApplied]);

  useEffect(() => {
    if (restartAnimation) {
      resetTransactionBox();
      onRestart && onRestart();
    }
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restartAnimation]);

  const resetTransactionBox = () => {
    setTagColor(baseColor);
    setTagOpacity("0.5");
    setTimeout(() => {
      resetCounter();
    }, 250);
  };

  useEffect(() => {
    if (counter >= maxAmount) {
      setTimeout(() => {
        setTagColor(transitionColor);
        setTagOpacity("1");
      }, 500);
    }
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter]);

  return (
    <Box
      gutterV={1}
      gutterH={1.5}
      elevation={2}
      bg={Color.White}
      borderRadius={0.5}
      style={{
        transform: `scale(${TRANSACTION_BOX_DEFAULT_SCALE},${TRANSACTION_BOX_DEFAULT_SCALE})`,
      }}
      css={isAnimationApplied ? animation : {}}
      onAnimationEnd={() => {
        if (onAnimationFinished) {
          onAnimationFinished();
        }
      }}
      width={295}
    >
      <Box gutterV={0.25}>
        <Text size={2}>{title}</Text>
        <Space v={0.25} />
        <MoneyText
          size={8}
          css={{ animation: "1s ease-in-out" }}
          amount={counter}
          weight={FontWeight.SemiBold}
          currency={Currency.Gbp}
          locale={locale}
          format="short"
        />
        <Space v={0.25} />
        <Box
          layout="flex"
          justifyContent={JustifyContent.SpaceBetween}
          alignItems={AlignItems.Center}
        >
          <Tag
            css={{
              transition: "opacity 300ms linear",
              backfaceVisibility: "hidden",
              opacity: tagOpacity,
            }}
            color={tagColor}
            size={TagSize.Md}
            colorScheme={ColorScheme.OnLight}
            variant={TagVariant.Solid}
          >
            {paymentSchedule}
          </Tag>
          <Text
            css={{
              fontSize: "14px",
              lineHeight: "10px",
              verticalAlign: "middle",
            }}
            color={ColorPreset.TextOnLight_03}
          >
            {paymentType}
          </Text>
        </Box>
        <Space v={0.25} />
      </Box>
    </Box>
  );
};
